import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalPersistStorage } from '@/stores/persistStorage';
import { useAuthStore } from '@/stores/authStore';
import { writeRoutine, deleteRoutine as fsDeleteRoutine, writeRun } from '@/stores/sync';
import type { Routine, RoutineRun } from '@/types';

function syncRoutine(routine: Routine) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writeRoutine(uid, routine);
  }
}

function syncDeleteRoutine(routineId: string) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void fsDeleteRoutine(uid, routineId);
  }
}

function syncRun(run: RoutineRun) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writeRun(uid, run);
  }
}

export interface RoutineState {
  routines: Routine[];
  runs: RoutineRun[];
  addRoutine: (routine: Routine) => void;
  updateRoutine: (id: string, patch: Partial<Omit<Routine, 'id' | 'createdAt'>>) => void;
  deleteRoutine: (id: string) => void;
  recordRun: (run: RoutineRun) => void;
  runsForRoutine: (routineId: string) => RoutineRun[];
}

export const useRoutineStore = create<RoutineState>()(
  persist(
    (set, get) => ({
      routines: [],
      runs: [],

      addRoutine: (routine) => {
        set((s) => ({ routines: [...s.routines, routine] }));
        syncRoutine(routine);
      },

      updateRoutine: (id, patch) => {
        set((s) => ({
          routines: s.routines.map((r) =>
            r.id === id ? { ...r, ...patch, updatedAt: new Date() } : r,
          ),
        }));
        const updated = get().routines.find((r) => r.id === id);
        if (updated) syncRoutine(updated);
      },

      deleteRoutine: (id) => {
        set((s) => ({ routines: s.routines.filter((r) => r.id !== id) }));
        syncDeleteRoutine(id);
      },

      recordRun: (run) => {
        set((s) => ({ runs: [...s.runs, run] }));
        syncRun(run);
      },

      runsForRoutine: (routineId) =>
        [...get().runs]
          .filter((r) => r.routineId === routineId)
          .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime()),
    }),
    {
      name: 'brainday:routineStore',
      storage: conditionalPersistStorage<RoutineState>(),
    },
  ),
);
